import { motion } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { DURATION } from './motion'

/**
 * `brass` is for REWARD ACTIONS ONLY — Claim, and nothing else.
 *
 * The deck reserves brass for reward, yield and caps, so a brass button that does not pay the
 * user out breaks the meaning of the colour everywhere else it appears. If a new action wants
 * to look important, it wants `primary`.
 */
type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'brass'
type Size = 'sm' | 'md' | 'lg'

/**
 * Enamel and brass are MATERIALS, so they come from gradient tokens and inset shadows rather
 * than flat fills. That is the difference between a control that looks pressed into a panel
 * and one that looks painted onto a page — and on a light canvas, where a flat blue rectangle
 * has nothing to distinguish it from a link, it is doing real work.
 */
const VARIANTS: Record<Variant, string> = {
  primary: 'text-accent-ink [background:var(--btn-primary-bg)] [box-shadow:var(--btn-primary-shadow)] hover:[box-shadow:var(--btn-primary-shadow-hi)]',
  secondary: 'text-ink [background:var(--btn-ghost-bg)] [box-shadow:var(--btn-ghost-shadow)] hover:[background:var(--btn-ghost-bg-hi)]',
  ghost: 'bg-transparent text-muted hover:bg-accent-wash hover:text-ink',
  danger: 'bg-danger text-white hover:opacity-90',
  brass: '[color:var(--btn-brass-fg)] [background:var(--btn-brass-bg)] [box-shadow:var(--btn-brass-shadow)] [text-shadow:var(--btn-brass-textshadow)]',
}

const SIZES: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm rounded-[2px] gap-1.5',
  md: 'h-10 px-4 text-sm rounded-[10px] gap-2',
  lg: 'h-12 px-6 text-base rounded-[10px] gap-2',
}

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onAnimationStart' | 'onDragStart' | 'onDragEnd' | 'onDrag'> {
  variant?: Variant
  size?: Size
  loading?: boolean
  fullWidth?: boolean
  iconLeft?: ReactNode
  iconRight?: ReactNode
}

/**
 * The one button.
 *
 * `loading` disables interaction as well as showing the spinner — a user who can
 * double-submit a bond transaction because the button was merely *styled* as busy
 * has been failed by the component, not by themselves.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    fullWidth = false,
    iconLeft,
    iconRight,
    className,
    children,
    disabled,
    ...props
  },
  ref,
) {
  const isDisabled = disabled || loading

  return (
    <motion.button
      ref={ref}
      whileHover={isDisabled ? undefined : { y: -1 }}
      whileTap={isDisabled ? undefined : { scale: 0.985 }}
      transition={{ duration: DURATION.instant }}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center font-medium select-none',
        'transition-[box-shadow,background,color] duration-150',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : iconLeft}
      {children}
      {!loading && iconRight}
    </motion.button>
  )
})
